import { Injectable, computed, inject, signal } from '@angular/core';
import { AppStateService } from './app-state.service';

interface LayoutState {
  /** Desktop: the side menu is folded away. */
  staticMenuDesktopInactive: boolean;
  /** Small screens: the side menu is slid over the page. */
  mobileMenuActive: boolean;
  /** True only for a moment after the menu toggle is pressed, so the page animates then and at no other time. */
  menuToggling: boolean;
}

/**
 * State of the page frame: the side menu (open or folded, on a desktop or a phone) and the
 * light/dark theme. The theme itself is kept by AppStateService, which also sets the `dark`
 * class on <html> that both Tailwind's dark: variant and the PrimeNG theme listen to.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  private readonly appState = inject(AppStateService);

  readonly layoutState = signal<LayoutState>({
    staticMenuDesktopInactive: false,
    mobileMenuActive: false,
    menuToggling: false,
  });

  private toggleTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isDarkTheme = computed(() => this.appState.state().theme === 'dark');

  /** The theme settings panel (the gear in the top bar) is open. */
  readonly settingsOpen = signal(false);

  openSettings(): void {
    this.settingsOpen.set(true);
  }

  closeSettings(): void {
    this.settingsOpen.set(false);
  }

  /** Sets light or dark (a no-op when it already is). */
  setTheme(mode: 'light' | 'dark'): void {
    if ((mode === 'dark') !== this.isDarkTheme()) {
      this.toggleTheme();
    }
  }

  isDesktop(): boolean {
    return window.innerWidth > 991;
  }

  /** The menu toggle in the top bar: folds the menu on a desktop, slides it over the page on a phone. */
  onMenuToggle(): void {
    if (this.isDesktop()) {
      this.layoutState.update((s) => ({ ...s, staticMenuDesktopInactive: !s.staticMenuDesktopInactive, menuToggling: true }));
      if (this.toggleTimer) clearTimeout(this.toggleTimer);
      this.toggleTimer = setTimeout(() => this.layoutState.update((s) => ({ ...s, menuToggling: false })), 500);
    } else {
      this.layoutState.update((s) => ({ ...s, mobileMenuActive: !s.mobileMenuActive }));
    }
  }

  /** Whether the side menu is showing now, for the toggle's icon: it is a cross while the menu is open. */
  isMenuOpen(): boolean {
    const state = this.layoutState();
    return this.isDesktop() ? !state.staticMenuDesktopInactive : state.mobileMenuActive;
  }

  /** A desktop with the menu folded: the sidebar stays as a narrow rail of icons (not hidden). */
  isRail(): boolean {
    return this.isDesktop() && this.layoutState().staticMenuDesktopInactive;
  }

  /** Closes the menu on a small screen (the mask behind it, a link chosen). */
  closeMobileMenu(): void {
    if (this.layoutState().mobileMenuActive) {
      this.layoutState.update((s) => ({ ...s, mobileMenuActive: false }));
    }
  }

  /** Light to dark or back; AppStateService remembers it for next time. */
  toggleTheme(): void {
    const theme = this.isDarkTheme() ? 'light' : 'dark';
    const apply = () => {
      this.appState.updateState({ theme, themeBackground: '', headerColor: theme, menuColor: 'dark' });
      const html = document.documentElement;
      for (const name of ['--color-bodybg', '--color-bodybg2', '--color-light', '--color-formcontrolbg', '--color-inputborder', '--color-gray3']) {
        html.style.removeProperty(name);
      }
    };
    const withTransition = (document as Document & { startViewTransition?: (cb: () => void) => unknown }).startViewTransition;
    if (typeof withTransition === 'function') {
      withTransition.call(document, apply);
    } else {
      apply();
    }
  }
}
